<!--
  BannerTable.vue
  배너 테이블 컴포넌트
  
  주요 기능:
  - 배너 목록 표시
  - 이미지 미리보기
  - 노출 여부 토글
  - 수정/삭제 기능
-->
<template>
  <div class="banner-table-container">
    <!-- 테이블 상단 액션 영역 -->
    <div class="table-header">
      <div class="table-info">
        <span class="total-count">
          총 <strong>{{ pagination.total }}</strong>개의 배너
        </span>
      </div>
      <div class="table-actions">
        <a-button 
          type="primary" 
          class="yellow-button"
          @click="handleAdd"
        >
          <i class="fa-solid fa-plus"></i>
          배너 추가
        </a-button>
      </div>
    </div>

    

    <!-- 배너 테이블 (드래그 앤 드롭 지원) -->
    <div class="draggable-table-container">
      <div class="table-header-row">
        <div class="table-cell drag-cell"></div>
        <div class="table-cell index-cell">번호</div>
        <div class="table-cell preview-cell">미리보기</div>
        <div class="table-cell name-cell">배너명</div>
        <div class="table-cell link-cell">링크URL</div>
        <div class="table-cell period-cell">노출 기간</div>
        <div class="table-cell visible-cell">노출 여부</div>
        <div class="table-cell click-cell">클릭수</div>
        <div class="table-cell date-cell">등록일</div>
        <div class="table-cell action-cell">관리</div>
      </div>
      
      <!-- 로딩 상태 -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span>데이터를 불러오는 중입니다...</span>
        </div>
      </div>
      
      <!-- 데이터 없음 -->
      <div v-else-if="bannerList.length === 0" class="no-data">
        <span>등록된 배너가 없습니다.</span>
      </div>
      
      <!-- 드래그 가능한 배너 리스트 -->
      <draggable
        v-else
        v-model="internalBannerList"
        class="draggable-list"
        ghost-class="ghost-item"
        handle=".drag-handle"
        @end="handleDragEnd"
      >
        <template #item="{ element, index }">
          <div class="table-row">
            <!-- 드래그 핸들 -->
            <div class="table-cell drag-cell">
              <div class="drag-handle">
                <i class="fa-solid fa-grip-vertical"></i>
              </div>
            </div>
            
            <!-- 번호 -->
            <div class="table-cell index-cell">
              {{ (pagination.current - 1) * pagination.pageSize + index + 1 }}
            </div>
            
            <!-- 미리보기 -->
            <div class="table-cell preview-cell">
              <div class="preview-container">
                <img 
                  v-if="element.bannerLink" 
                  :src="element.bannerLink" 
                  :alt="element.bannerName"
                  class="preview-image"
                  @error="handleImageError"
                  @click="handleImagePreview(element.bannerLink, element.bannerName)"
                />
                <div v-else class="no-image">
                  <i class="fa-solid fa-image"></i>
                  <span>이미지 없음</span>
                </div>
              </div>
            </div>
            
            <!-- 배너명 -->
            <div class="table-cell name-cell">
              <div class="banner-name">
                <span class="name-text">{{ element.bannerName || '-' }}</span>
              </div>
            </div>
            
            <!-- 링크URL -->
            <div class="table-cell link-cell">
              <div class="link-url">
                <a 
                  v-if="element.linkUrl" 
                  :href="element.linkUrl" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="url-link"
                >
                  {{ truncateUrl(element.linkUrl) }}
                  <i class="fa-solid fa-external-link-alt"></i>
                </a>
                <span v-else class="no-link">-</span>
              </div>
            </div>
            
            <!-- 노출 기간 -->
            <div class="table-cell period-cell">
              <div class="display-period">
                <div class="period-item">
                  <span class="period-label">시작:</span>
                  <span class="period-date">{{ formatDate(element.displayStartDate) }}</span>
                </div>
                <div class="period-item">
                  <span class="period-label">종료:</span>
                  <span class="period-date">{{ formatDate(element.displayEndDate) }}</span>
                </div>
              </div>
            </div>
            
            <!-- 노출 여부 -->
            <div class="table-cell visible-cell">
              <div class="display-status">
                <a-switch
                  :checked="element.isVisible === 'Y'"
                  :loading="element.toggleLoading"
                  @change="(checked) => handleToggleDisplay(element, element.isVisible)"
                />
                <span :class="['status-text', element.isVisible === 'Y' ? 'active' : 'inactive']">
                  {{ element.isVisible === 'Y' ? '노출' : '비노출' }}
                </span>
              </div>
            </div>
            
            <!-- 클릭수 -->
            <div class="table-cell click-cell">
              <div class="click-count">
                <span class="count-number">{{ formatCommaNumber(element.clickCount || 0) }}</span>
                <span class="count-unit">회</span>
              </div>
            </div>
            
            <!-- 등록일 -->
            <div class="table-cell date-cell">
              <div class="created-date">
                {{ formatDateTime(element.createAt) }}
              </div>
            </div>
            
            <!-- 관리 -->
            <div class="table-cell action-cell">
              <div class="action-buttons">
                <a-button 
                  size="small" 
                  type="primary"
                  class="yellow-button"
                  @click="handleEdit(element)"
                >
                  <i class="fa-solid fa-edit"></i>
                  수정
                </a-button>
                <a-button 
                  size="small" 
                  danger
                  @click="handleDelete(element)"
                >
                  <i class="fa-solid fa-trash"></i>
                  삭제
                </a-button>
              </div>
            </div>
          </div>
        </template>
      </draggable>
      
      <!-- 페이지네이션 -->
      <div v-if="bannerList.length > 0" class="pagination-container">
        <a-pagination
          v-model:current="pagination.current"
          v-model:pageSize="pagination.pageSize"
          :total="pagination.total"
          :show-size-changer="true"
          :show-quick-jumper="true"
          :show-total="(total, range) => `${range[0]}-${range[1]} / 총 ${total}개`"
          @change="handleTableChange"
        />
      </div>
    </div>

    <!-- 기존 테이블 (백업용 - 제거 예정) -->
    <a-table
      v-if="false"
      :columns="columns"
      :dataSource="bannerList"
      :loading="loading"
      :pagination="paginationConfig"
      rowKey="_id"
      class="banner-table"
      @change="handleTableChange"
    >
      <!-- 미리보기 컬럼 -->
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'preview'">
          <div class="preview-container">
            <img 
              v-if="record.bannerLink" 
              :src="record.bannerLink" 
              :alt="record.bannerName"
              class="preview-image"
              @error="handleImageError"
              @click="handleImagePreview(record.bannerLink, record.bannerName)"
            />
            <div v-else class="no-image">
              <i class="fa-solid fa-image"></i>
              <span>이미지 없음</span>
            </div>
          </div>
        </template>
        
        <!-- 배너명 컬럼 -->
        <template v-else-if="column.key === 'bannerName'">
          <div class="banner-name">
            <span class="name-text">{{ record.bannerName || '-' }}</span>
          </div>
        </template>
        
        <!-- 링크URL 컬럼 -->
        <template v-else-if="column.key === 'linkUrl'">
          <div class="link-url">
            <a 
              v-if="record.linkUrl" 
              :href="record.linkUrl" 
              target="_blank" 
              rel="noopener noreferrer"
              class="url-link"
            >
              {{ truncateUrl(record.linkUrl) }}
              <i class="fa-solid fa-external-link-alt"></i>
            </a>
            <span v-else class="no-link">-</span>
          </div>
        </template>
        
        <!-- 노출 기간 컬럼 -->
        <template v-else-if="column.key === 'displayPeriod'">
          <div class="display-period">
            <div class="period-item">
              <span class="period-label">시작:</span>
              <span class="period-date">{{ formatDate(record.displayStartDate) }}</span>
            </div>
            <div class="period-item">
              <span class="period-label">종료:</span>
              <span class="period-date">{{ formatDate(record.displayEndDate) }}</span>
            </div>
          </div>
        </template>
        
        <!-- 노출 여부 컬럼 -->
        <template v-else-if="column.key === 'isVisible'">
          <div class="display-status">
            <a-switch
              :checked="record.isVisible === 'Y'"
              :loading="record.toggleLoading"
              @change="(checked) => handleToggleDisplay(record, record.isVisible)"
            />
            <span :class="['status-text', record.isVisible === 'Y' ? 'active' : 'inactive']">
              {{ record.isVisible === 'Y' ? '노출' : '비노출' }}
            </span>
          </div>
        </template>
        
        <!-- 클릭수 컬럼 -->
        <template v-else-if="column.key === 'clickCount'">
          <div class="click-count">
            <span class="count-number">{{ formatCommaNumber(record.clickCount || 0) }}</span>
            <span class="count-unit">회</span>
          </div>
        </template>
        
        <!-- 등록일 컬럼 -->
        <template v-else-if="column.key === 'createAt'">
          <div class="created-date">
            {{ formatDateTime(record.createAt) }}
          </div>
        </template>
        
        <!-- 관리 컬럼 -->
        <template v-else-if="column.key === 'actions'">
          <div class="action-buttons">
            <a-button 
              size="small" 
              type="primary"
              class="yellow-button"
              @click="handleEdit(record)"
            >
              <i class="fa-solid fa-edit"></i>
              수정
            </a-button>
            <a-button 
              size="small" 
              danger
              @click="handleDelete(record)"
            >
              <i class="fa-solid fa-trash"></i>
              삭제
            </a-button>
          </div>
        </template>
      </template>
    </a-table>

    <!-- 이미지 미리보기 모달 -->
    <a-modal
      v-model:open="previewModal.visible"
      :title="previewModal.title"
      :footer="null"
      width="80%"
      centered
      class="image-preview-modal"
    >
      <div class="image-preview-content">
        <img 
          :src="previewModal.imageUrl" 
          :alt="previewModal.title"
          class="preview-full-image"
        />
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, computed, reactive } from 'vue';
import { message } from 'ant-design-vue';
import { formatCommaNumber, convertDate, convertToLocalTime } from '@/utils/utils';
import draggable from 'vuedraggable';

export default {
  name: 'BannerTable',
  components: {
    draggable
  },
  props: {
    bannerList: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({
        current: 1,
        pageSize: 10,
        total: 0
      })
    }
  },
  emits: ['add', 'edit', 'delete', 'toggle-display', 'table-change', 'drag-end', 'update:bannerList'],
  setup(props, { emit }) {
    // 이미지 미리보기 모달 상태
    const previewModal = reactive({
      visible: false,
      imageUrl: '',
      title: ''
    });

    // 드래그 앤 드롭을 위한 내부 배너 리스트
    const internalBannerList = computed({
      get() {
        return props.bannerList;
      },
      set(newVal) {
        emit('update:bannerList', newVal);
      }
    });
    
    // 테이블 컬럼 정의
    const columns = [
      {
        title: '',
        key: 'drag',
        align: 'center'
      },
      {
        title: '번호',
        dataIndex: 'index',
        key: 'index',
        align: 'center'
      },
      {
        title: '미리보기',
        key: 'preview',
        align: 'center'
      },
      {
        title: '배너명',
        dataIndex: 'bannerName',
        key: 'bannerName',
        ellipsis: true
      },
      {
        title: '링크URL',
        dataIndex: 'linkUrl',
        key: 'linkUrl',
        ellipsis: true
      },
      {
        title: '노출 기간',
        key: 'displayPeriod',
        align: 'center'
      },
      {
        title: '노출 여부',
        key: 'isVisible',
        align: 'center'
      },
      {
        title: '클릭수',
        dataIndex: 'clickCount',
        key: 'clickCount',
        align: 'center'
      },
      {
        title: '등록일',
        dataIndex: 'createAt',
        key: 'createAt',
        align: 'center'
      },
      {
        title: '관리',
        key: 'actions',
        align: 'center',
        fixed: 'center'
      }
    ];

    // 페이지네이션 설정
    const paginationConfig = computed(() => ({
      ...props.pagination,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`
    }));

    // URL 텍스트 줄이기
    const truncateUrl = (url) => {
      if (!url) return '';
      return url.length > 40 ? url.substring(0, 40) + '...' : url;
    };

    // 날짜 포맷팅
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return convertDate(dateString);
    };

    // 날짜시간 포맷팅
    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      return convertToLocalTime(dateString);
    };

    // 이미지 오류 처리
    const handleImageError = (event) => {
      event.target.style.display = 'none';
      const nextElement = event.target.nextElementSibling;
      if (nextElement) {
        nextElement.style.display = 'flex';
      }
    };

    // 이미지 미리보기 핸들러
    const handleImagePreview = (imageUrl, bannerName) => {
      if (!imageUrl) return;
      
      Object.assign(previewModal, {
        visible: true,
        imageUrl,
        title: `배너 미리보기: ${bannerName || '제목 없음'}`
      });
    };

    // 이벤트 핸들러
    const handleAdd = () => {
      emit('add');
    };

    const handleEdit = (record) => {
      emit('edit', record);
    };

    const handleDelete = (record) => {
      emit('delete', record._id || record.id);
    };

    const handleToggleDisplay = (record, checked) => {
      emit('toggle-display', record._id || record.id, checked);
    };

    const handleTableChange = (pagination, filters, sorter) => {
      emit('table-change', pagination, filters, sorter);
    };

    // 드래그 앤 드롭 핸들러
    const handleDragEnd = (event) => {
      emit('drag-end', {
        newIndex: event.newIndex,
        oldIndex: event.oldIndex,
        item: internalBannerList.value[event.newIndex]
      });
    };

    return {
      columns,
      paginationConfig,
      previewModal,
      internalBannerList,
      truncateUrl,
      formatDate,
      formatDateTime,
      formatCommaNumber,
      handleImageError,
      handleImagePreview,
      handleAdd,
      handleEdit,
      handleDelete,
      handleToggleDisplay,
      handleTableChange,
      handleDragEnd
    };
  }
};
</script>

<style scoped lang="scss">
.banner-table-container {
  padding: 20px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.table-info {
  .total-count {
    font-size: 14px;
    color: #666;
    
    strong {
      color: #333;
      font-weight: 600;
    }
  }
}

.table-actions {
  display: flex;
  gap: 8px;
}

// 미리보기 스타일
.preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80px;
  height: 60px;
  margin: 0 auto;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
  background: #fafafa;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: scale(1.05);
  }
}

.no-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 12px;
  
  i {
    font-size: 20px;
    margin-bottom: 4px;
  }
}

// 배너명 스타일
.banner-name {
  .name-text {
    font-weight: 500;
    color: #333;
  }
}

// 링크URL 스타일
.link-url {
  .url-link {
    color: #1890ff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
    
    i {
      margin-left: 4px;
      font-size: 12px;
    }
  }
  
  .no-link {
    color: #999;
  }
}

// 노출 기간 스타일
.display-period {
  font-size: 12px;
  
  .period-item {
    display: flex;
    align-items: center;
    margin-bottom: 2px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .period-label {
    color: #666;
    min-width: 30px;
    margin-right: 4px;
  }
  
  .period-date {
    color: #333;
    font-weight: 500;
  }
}

// 노출 여부 스타일
.display-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  
  .status-text {
    font-size: 12px;
    font-weight: 500;
    
    &.active {
      color: #52c41a;
    }
    
    &.inactive {
      color: #f5222d;
    }
  }
}

// 클릭수 스타일
.click-count {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
  
  .count-number {
    font-weight: 600;
    color: #333;
  }
  
  .count-unit {
    font-size: 12px;
    color: #666;
  }
}

// 등록일 스타일
.created-date {
  font-size: 12px;
  color: #666;
}

// 액션 버튼 스타일
.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

// 공통 버튼 스타일
:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 테이블 스타일
:deep(.banner-table) {
  .ant-table-thead > tr > th {
    background-color: #fafafa;
    font-weight: 600;
    color: #333;
    border-bottom: 2px solid #f0f0f0;
  }
  
  .ant-table-tbody > tr > td {
    padding: 12px 8px;
    vertical-align: middle;
  }
  
  .ant-table-tbody > tr:hover > td {
    background-color: #f5f5f5;
  }
}

// 이미지 미리보기 모달 스타일
:deep(.image-preview-modal) {
  .ant-modal-content {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .ant-modal-header {
    border-bottom: 1px solid #f0f0f0;
    padding: 16px 24px;
    
    .ant-modal-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }
  
  .ant-modal-body {
    padding: 0;
  }
  
  .ant-modal-close {
    top: 16px;
    right: 16px;
    
    .ant-modal-close-x {
      width: 32px;
      height: 32px;
      line-height: 32px;
      font-size: 16px;
      color: #666;
      
      &:hover {
        color: #333;
      }
    }
  }
}

.image-preview-content {
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;
  min-height: 400px;
  max-height: 80vh;
  overflow: hidden;
  padding: 20px;
}

.preview-full-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

// 드래그 가능한 테이블 스타일
.draggable-table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header-row {
  display: flex;
  background-color: #fafafa;
  border-bottom: 2px solid #f0f0f0;
  font-weight: 600;
  color: #333;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  &:last-child {
    border-bottom: none;
  }
}

.table-cell {
  padding: 12px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #f0f0f0;
  
  &:last-child {
    border-right: none;
  }
}

// 각 컬럼별 너비 설정
.drag-cell {
  flex: 0 0 40px;
  
  .drag-handle {
    cursor: grab;
    color: #666;
    padding: 4px;
    border-radius: 4px;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: #e6f7ff;
      color: #1890ff;
    }
    
    &:active {
      cursor: grabbing;
    }
  }
}

.index-cell { flex: 0 0 60px; }
.preview-cell { flex: 0 0 120px; }
.name-cell { flex: 0 0 200px; }
.link-cell { flex: 0 0 350px; }
.period-cell { flex: 0 0 180px; }
.visible-cell { flex: 0 0 120px; }
.click-cell { flex: 0 0 100px; }
.date-cell { flex: 0 0 150px; }
/* .action-cell { flex: 0 0 100px; } */

// 로딩 및 빈 상태 스타일
.loading-container, .no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #666;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  
  i {
    font-size: 24px;
    color: #1890ff;
  }
}

// 드래그 앤 드롭 스타일
.draggable-list {
  min-height: 100px;
}

.ghost-item {
  opacity: 0.5;
  background: #f0f8ff;
  border: 1px dashed #1890ff;
}

// 페이지네이션 스타일
.pagination-container {
  padding: 16px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  display: flex;
  justify-content: center;
}

</style> 